/** Forked from from mantine usemove  * https://github.com/mantinedev/mantine/blob/master/src/mantine-hooks/src/use-move/use-move.ts#L36
 * I wanted the option not to clamp between bounds, and to be able to drag
 */

import { DateTime } from 'luxon';
import { useEffect, useRef, useCallback } from 'react';
import { clamp } from 'remeda';
import { doSafe, doTry } from 'shared/promise';
export interface UseMovePosition {
  x: number;
  y: number;
}
const deltaPosish = (end: UseMovePosition, start: UseMovePosition) => ({
  x: end.x - start.x,
  y: end.y - start.y
});
export type OnDragProps = {
  active: boolean;
  posish: UseMovePosition;
  delta: UseMovePosition;
};
export function useDrag<T extends HTMLElement = HTMLDivElement>(options: {
  onChange?: (props: OnDragProps) => void;
  // when true, x and y will be clamped between 0 and 1
  normalize?: boolean;
  shouldClamp?: boolean;
  minDistance?: number;
  onStart?: (pos: UseMovePosition) => void;
  onEnd?: () => void;
  /** called when it was more of a click than a drag */
  sansDragClick?: (e: React.MouseEvent<T>) => void;
  disabled?: boolean;
}) {
  const {
    shouldClamp,
    normalize,
    onStart,
    onEnd,
    minDistance,
    sansDragClick,
    onChange,
    disabled
  } = {
    shouldClamp: true,
    minDistance: 0.01,
    normalize: true,
    ...options
  };
  const ref = useRef<T>(null);
  const mounted = useRef<boolean>(false);
  const isDragging = useRef(false);
  const isScrubbing = useRef(false);
  const cancelRequested = useRef(false);
  const dragLastEnded = useRef<DateTime | undefined>(undefined);
  const frame = useRef(0);
  const active = useRef(false);
  const startPosish = useRef<UseMovePosition | undefined>(undefined);
  const clampRef = useRef(shouldClamp);
  clampRef.current = shouldClamp;
  useEffect(() => {
    if (onChange) mounted.current = true;
  }, [onChange]);

  /** cancels the drag, and doesn't call onClick. This is tricky to use. it must be:
   * native events (i.e. ref.addEventListener) and either mouseDown, or in the cpaturePhase of MouseUp
   */
  const cancel = useCallback(() => {
    if (isScrubbing.current) {
      cancelRequested.current = true;
    }
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return;
    const clampPosish = (v: number) => clampRef.current ? clamp(v, {
      min: 0,
      max: 1
    }) : v;
    const onScrub = ({
      x,
      y
    }: UseMovePosition) => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        if (cancelRequested.current) {
          stopScrubbing();
          return;
        }
        if (mounted.current && ref.current && isScrubbing.current) {
          const rect = ref.current.getBoundingClientRect();
          if (rect.width && rect.height) {
            const posish = normalize ? {
              x: clampPosish((x - rect.left) / rect.width),
              y: clampPosish((y - rect.top) / rect.height)
            } : {
              x: x - rect.left,
              y: y - rect.top
            };
            if (!startPosish.current) startPosish.current = posish;
            const delta = deltaPosish(posish, startPosish.current);
            const isStartDrag = !isDragging.current && Math.sqrt(delta.x ** 2 + delta.y ** 2) > minDistance;
            if (isStartDrag) {
              isDragging.current = true;
              onStart?.(posish);
            }
            if (isDragging.current) onChange?.({
              active: active.current,
              posish: posish,
              delta
            });
          }
        }
      });
    };
    const bindEvents = () => {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', stopScrubbing as any, {
        capture: false
      });
      document.addEventListener('touchmove', onTouchMove); //, {passive: false}
      document.addEventListener('touchend', stopScrubbing as any, {
        capture: false
      });
    };
    const unbindEvents = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stopScrubbing as any);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', stopScrubbing as any);
    };
    const startScrubbing = () => {
      if (!isScrubbing.current && mounted.current) {
        isScrubbing.current = true;
        active.current = true;
        bindEvents();
      }
    };
    const stopScrubbing = (e?: MouseOrTouch) => {
      if (isScrubbing.current && mounted.current) {
        isScrubbing.current = false;
        active.current = false;
        startPosish.current = undefined;
        unbindEvents();
        if (isDragging.current) {
          onEnd?.();
          isDragging.current = false;
          dragLastEnded.current = DateTime.now();
        }
      }
    };
    const onMouseDown = () => startScrubbing();
    const onMouseMove = (event: MouseEvent) => onScrub({
      x: event.clientX,
      y: event.clientY
    });
    const onTouchStart = () => startScrubbing();
    const onTouchMove = (event_0: TouchEvent) => {
      if (event_0.cancelable) doTry(() => event_0.preventDefault());
      const touch = event_0.changedTouches[0];
      if (!touch) return;
      onScrub({
        x: touch?.clientX,
        y: touch?.clientY
      });
    };
    const onClick = (e_0: React.MouseEvent<T>) => {
      // only call onClick if there wasn't a drag that just ended (under 10ms)
      if (!cancelRequested.current && !(dragLastEnded.current != null && dragLastEnded.current.diffNow().milliseconds < 10)) {
        sansDragClick?.(e_0);
      }
      cancelRequested.current = false;
      dragLastEnded.current = undefined;
    };

    // only call onClick if there wasn't a
    el.addEventListener('click', onClick as any);
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('touchstart', onTouchStart, {
      passive: false
    });
    return () => {
      if (el) {
        el.removeEventListener('click', onClick as any);
        el.removeEventListener('mousedown', onMouseDown);
        el.removeEventListener('touchstart', onTouchStart);
      }
    };
  }, [ref.current, onChange, options?.normalize, options?.shouldClamp, disabled]);
  return {
    ref,
    active,
    cancel,
    disabled
  };
}
type MouseOrTouch = MouseEvent | TouchEvent;
const isTouch = (event: MouseOrTouch): event is TouchEvent => event.type.startsWith('touch');

/** Takes the given event handler and calls it only when the click isn't part of a drag */
export const useClickSansDrag = <T extends HTMLElement = HTMLDivElement,>(onClick: (event: MouseEvent) => void) => {
  const ref = useRef<T>(null);
  const dragging = useRef(false); // we we currently dragging
  const dragStartRef = useRef<{
    x: number;
    y: number;
  } | undefined>(undefined); // the location where the drag started
  const lastDraggedRef = useRef<DateTime | undefined>(undefined); // the moment in time that we last ended dragging

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleMouseDown = (event: MouseOrTouch) => {
      const location = isTouch(event) ? doSafe(event.changedTouches[0], t => ({
        x: t.clientX,
        y: t.clientY
      })) : {
        x: event.clientX,
        y: event.clientY
      };
      if (location) dragStartRef.current = location;
    };
    const handleMouseUp = (event_0: MouseOrTouch) => {
      dragStartRef.current = undefined;
      lastDraggedRef.current = DateTime.now();
    };
    const handleMouseMove = (event_1: MouseEvent) => {
      if (!dragStartRef.current) return;
      const threshold = 5;
      const dx = event_1.clientX - dragStartRef.current.x;
      const dy = event_1.clientY - dragStartRef.current.y;
      if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) dragging.current = true;
    };
    const handleTouchMove = (event_2: TouchEvent) => {
      const ct = event_2.changedTouches[0];
      if (!dragStartRef.current || !ct) return;
      const threshold_0 = 5;
      const dx_0 = ct.clientX - dragStartRef.current.x;
      const dy_0 = ct.clientY - dragStartRef.current.y;
      if (Math.abs(dx_0) > threshold_0 || Math.abs(dy_0) > threshold_0) dragging.current = true;
    };
    const handleClick = (event_3: MouseEvent) => {
      if (dragging.current && lastDraggedRef.current && lastDraggedRef.current.diffNow().milliseconds < 10) {} else {
        onClick(event_3);
      }
      lastDraggedRef.current = undefined;
      dragging.current = false;
    };
    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('touchmove', handleTouchMove);
    el.addEventListener('mouseup', handleMouseUp as any);
    el.addEventListener('touchEnd', handleMouseUp as any);
    el.addEventListener('mousedown', handleMouseDown as any);
    el.addEventListener('touchstart', handleMouseDown as any);
    el.addEventListener('click', handleClick as any);
    return () => {
      if (!el) return;
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('mouseup', handleMouseUp as any);
      el.removeEventListener('touchEnd', handleMouseUp as any);
      el.removeEventListener('mousedown', handleMouseDown as any);
      el.removeEventListener('touchstart', handleMouseDown as any);
      el.removeEventListener('click', handleClick as any);
    };
  }, [ref.current]);
  return ref;
};